import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Input,
  Row,
  Spinner,
} from 'reactstrap';
import useNearbyPlaces from '../../hooks/useNearbyPlaces';
import { NEARBY_CATEGORIES, NearbyPlaceStatus } from '../../models/nearbyModel';

interface Props {
  propertyId: string;
  center?: { lat: number; lng: number };
}

const STATUS_BADGE: Record<NearbyPlaceStatus, { color: string; label: string }> = {
  not_searched: { color: 'secondary', label: 'Sin buscar' },
  found: { color: 'success', label: 'Verificado en Google' },
  not_found: { color: 'warning', label: 'No encontrado en Google' },
  saved: { color: 'info', label: 'Guardado' },
};

// Lugares cercanos con puntuación y ventajas de la zona (sección "Ubicación" del sitio).
// La IA (n8n: Gemini + Google Maps) sugiere los nombres; cada uno se resuelve en Places
// por nombre para obtener ubicación, nota y foto reales. Se genera una sola vez por
// propiedad y el admin revisa y edita todo antes de guardar.
const NearbyPlacesCard: React.FC<Props> = ({ propertyId, center }) => {
  const {
    places,
    advantages,
    generatedAt,
    isLoading,
    isSuggesting,
    busyIndex,
    suggest,
    searchPlace,
    updatePlace,
    uploadImage,
    copyGooglePhoto,
    addPlace,
    removePlace,
    setAdvantage,
    addAdvantage,
    removeAdvantage,
    save,
  } = useNearbyPlaces({ propertyId, center });

  const hasLocation = Boolean(center?.lat && center?.lng);
  const busy = isLoading || isSuggesting;

  const handleSuggest = () => {
    if (generatedAt) {
      const confirmed = window.confirm(
        'Esta propiedad ya tiene lugares generados. Regenerar reemplaza lo que ves (no lo guardado hasta que pulses Guardar). ¿Continuar?',
      );
      if (!confirmed) return;
    }
    suggest(Boolean(generatedAt));
  };

  return (
    <Card className="mt-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <div>
          <h3 className="mb-0">Lugares cercanos y ventajas de la zona</h3>
          <small className="text-muted">
            Se muestran en la sección "Ubicación" del detalle de la propiedad.
          </small>
        </div>
        <Button
          color="primary"
          type="button"
          disabled={!hasLocation || busy}
          onClick={handleSuggest}
        >
          {isSuggesting && <Spinner size="sm" className="mr-2" />}
          {generatedAt ? 'Regenerar con IA' : 'Sugerir con IA'}
        </Button>
      </CardHeader>

      <CardBody>
        {!hasLocation && (
          <Alert color="warning">
            Primero guarda la ubicación de la propiedad en el mapa; la sugerencia se
            hace a partir de esas coordenadas.
          </Alert>
        )}
        {isSuggesting && (
          <Alert color="info">
            Buscando lugares con IA y verificándolos en Google. Puede tardar hasta un
            minuto.
          </Alert>
        )}

        <h5 className="text-uppercase text-muted ls-1 mb-3">Lugares cercanos</h5>
        {places.length === 0 && !isSuggesting && (
          <p className="text-muted">
            Aún no hay lugares. Usa "Sugerir con IA" o agrégalos a mano.
          </p>
        )}

        {places.map((place, index) => {
          const badge = STATUS_BADGE[place.status];
          const preview = place.image || place.googlePhotoUrl;
          const isBusy = busyIndex === index;
          return (
            <Row key={place.id} className="align-items-start border-bottom pb-3 mb-3">
              <Col md="2" className="text-center">
                {preview ? (
                  <img
                    src={preview}
                    alt={place.name}
                    className="rounded"
                    style={{ width: 96, height: 96, objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="rounded bg-secondary d-flex align-items-center justify-content-center text-muted"
                    style={{ width: 96, height: 96, margin: '0 auto' }}
                  >
                    Sin foto
                  </div>
                )}
                {!place.image && place.googlePhotoUrl && (
                  <small className="d-block text-muted mt-1">Foto de Google (sin guardar)</small>
                )}
              </Col>

              <Col md="6">
                <Row>
                  <Col md="7" className="mb-2">
                    <Input
                      bsSize="sm"
                      placeholder="Nombre del lugar"
                      value={place.name}
                      onChange={(e) => updatePlace(index, { name: e.target.value })}
                    />
                  </Col>
                  <Col md="5" className="mb-2">
                    <Input
                      bsSize="sm"
                      type="select"
                      value={place.category}
                      onChange={(e) => updatePlace(index, { category: e.target.value })}
                    >
                      {NEARBY_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md="7" className="mb-2">
                    <Input
                      bsSize="sm"
                      placeholder="Dirección o referencia (ayuda a encontrar la sucursal correcta)"
                      value={place.address}
                      onChange={(e) => updatePlace(index, { address: e.target.value })}
                    />
                  </Col>
                  <Col md="5" className="mb-2">
                    <Input
                      bsSize="sm"
                      type="number"
                      min={0}
                      max={5}
                      step={0.1}
                      placeholder="Nota (0 a 5)"
                      value={place.rating ?? ''}
                      onChange={(e) =>
                        updatePlace(index, {
                          rating: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </Col>
                </Row>
                <Badge color={badge.color} pill>
                  {badge.label}
                </Badge>
                {place.lat !== null && place.lng !== null && (
                  <small className="text-muted ml-2">
                    {place.lat.toFixed(5)}, {place.lng.toFixed(5)}
                  </small>
                )}
              </Col>

              <Col md="4" className="text-right">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  type="button"
                  className="mb-1"
                  disabled={isBusy || busy || !place.name.trim()}
                  onClick={() => searchPlace(index, place)}
                >
                  {isBusy && <Spinner size="sm" className="mr-1" />}
                  Buscar en Google
                </Button>{' '}
                {place.googlePhotoUrl && !place.image && (
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    type="button"
                    className="mb-1"
                    disabled={isBusy}
                    onClick={() => copyGooglePhoto(index, place)}
                  >
                    Usar foto de Google
                  </Button>
                )}{' '}
                <label className="btn btn-sm btn-outline-secondary mb-1">
                  Subir foto
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadImage(index, file, file.name);
                      e.target.value = '';
                    }}
                  />
                </label>{' '}
                <Button
                  size="sm"
                  color="danger"
                  outline
                  type="button"
                  className="mb-1"
                  onClick={() => removePlace(index)}
                >
                  Quitar
                </Button>
              </Col>
            </Row>
          );
        })}

        <Button size="sm" color="secondary" outline type="button" onClick={addPlace}>
          + Agregar lugar
        </Button>

        <h5 className="text-uppercase text-muted ls-1 mb-3 mt-4">Ventajas de la zona</h5>
        {advantages.map((advantage, index) => (
          <Row key={index} className="mb-2">
            <Col md="10">
              <Input
                bsSize="sm"
                maxLength={200}
                placeholder="Ej: Multiplaza a pocos minutos en carro"
                value={advantage}
                onChange={(e) => setAdvantage(index, e.target.value)}
              />
            </Col>
            <Col md="2">
              <Button
                size="sm"
                color="danger"
                outline
                type="button"
                onClick={() => removeAdvantage(index)}
              >
                Quitar
              </Button>
            </Col>
          </Row>
        ))}
        <Button size="sm" color="secondary" outline type="button" onClick={addAdvantage}>
          + Agregar ventaja
        </Button>
      </CardBody>

      <CardFooter className="text-right">
        <Button color="primary" type="button" disabled={busy} onClick={save}>
          {isLoading && !isSuggesting && <Spinner size="sm" className="mr-2" />}
          Guardar lugares y ventajas
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NearbyPlacesCard;
